import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { ChatPresenceStatus, ChatStatus } from '@/lib/sdk-official/resources/chat'
import { useChat, useOptionalChat } from './ChatProvider'
import { MY_STATUS_DOT, MY_STATUS_KEY, presenceDotClass } from './chatStatus'

const OPTIONS: { value: ChatStatus; help: string }[] = [
  { value: 'AVAILABLE', help: 'status.availableHelp' },
  { value: 'BUSY', help: 'status.busyHelp' },
  { value: 'UNAVAILABLE', help: 'status.unavailableHelp' },
]

const PILL: Record<ChatStatus, string> = {
  AVAILABLE: 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100',
  BUSY: 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100',
  UNAVAILABLE: 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100',
}

/**
 * F-184: the chat status picker (Disponível, Ocupado, Indisponível), a pill with
 * a dot that opens the three options. It changes at once and saves; an API error
 * puts the previous status back and says so under the pill. The first time (the
 * "you are available by default" notice) a short hint sits under it.
 */
export const StatusSelect = ({ className, align = 'start', compact = false }: {
  className?: string
  align?: 'start' | 'end'
  /** Only the dot and the chevron (a narrow row): the status name moves to `title` and `aria-label`. */
  compact?: boolean
}) => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [error, setError] = React.useState(false)
  const current = chat.myStatus
  const away = chat.myShownStatus === 'AWAY'
  const showHint = chat.me?.availabilityNoticeSeen === false

  const label = `${t('status.label')}: ${t(MY_STATUS_KEY[current])}`
  /* Beside the tabs (`align="end"`), the notes float under the pill instead of pushing the row. */
  const floatingNote = align === 'end' ? 'absolute right-0 top-full z-10 w-60 rounded-md bg-white px-2 py-1 shadow-md ring-1 ring-gray-200' : ''

  const markSeen = () => chat.updateMe({ availabilityNoticeSeen: true }).catch(() => {})

  const choose = (next: ChatStatus) => {
    setError(false)
    if (next === current) {
      if (showHint) markSeen()
      return
    }
    chat.setChatStatus(next).catch(() => setError(true))
  }

  return (
    <div className={cn('relative', className)} data-chat-status-select>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={label}
            title={compact ? label : undefined}
            data-chat-status={current}
            data-chat-status-compact={compact || undefined}
            className={cn('inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', compact ? 'px-2' : 'px-2.5', PILL[current])}
          >
            <span className={cn('h-2 w-2 shrink-0 rounded-full', MY_STATUS_DOT[current])} />
            {!compact && t(MY_STATUS_KEY[current])}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="z-[60] w-64">
          {OPTIONS.map((option) => (
            <DropdownMenuItem key={option.value} onClick={() => choose(option.value)} data-chat-status-option={option.value.toLowerCase()}>
              <span className={cn('mr-2 h-2 w-2 shrink-0 rounded-full', MY_STATUS_DOT[option.value])} />
              <span className="flex-1">
                <span className="block text-sm">{t(MY_STATUS_KEY[option.value])}</span>
                <span className="block text-xs text-gray-500">{t(option.help)}</span>
              </span>
              {current === option.value && <Check className="ml-2 h-4 w-4 text-gray-500" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {away && !error && <p className={cn('mt-1 text-xs text-amber-700', floatingNote)} data-chat-status-away>{t('status.awayHelp')}</p>}
      {error && <p role="alert" className={cn('mt-1 text-xs text-red-600', floatingNote)} data-chat-status-error>{t('status.saveError')}</p>}
      {showHint && !error && (
        <div role="note" data-chat-status-hint className={cn('absolute top-full z-10 mt-2 w-60 rounded-lg theme-light bg-gray-900 px-3 py-2 text-xs text-white shadow-lg', align === 'end' ? 'right-0' : 'left-0')}>
          <span className={cn('absolute -top-1 h-2 w-2 rotate-45 theme-light bg-gray-900', align === 'end' ? 'right-4' : 'left-4')} aria-hidden />
          {current === 'UNAVAILABLE' ? t('status.hintOff') : t('status.hint')}
          <button type="button" onClick={markSeen} className="ml-2 font-semibold underline">{t('status.hintOk')}</button>
        </div>
      )}
    </div>
  )
}

/** My own status dot on an avatar's bottom-right corner, Ausente included (nothing while the chat is off). */
export const MyStatusDot = ({ className }: { className?: string }) => {
  const chat = useOptionalChat()
  if (!chat?.enabled) return null
  return (
    <span
      aria-hidden
      data-my-status-dot={chat.myShownStatus}
      className={cn('absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white', MY_STATUS_DOT[chat.myShownStatus], className)}
    />
  )
}

/**
 * Someone else's dot: green online, yellow Ausente, red Ocupado. `always` keeps a gray dot
 * offline, which is also how Indisponivel and a block look (the API sends the same null).
 */
export const PresenceDot = ({ online, status, always = false, className }: { online?: boolean | null; status?: ChatPresenceStatus | null; always?: boolean; className?: string }) => {
  const color = presenceDotClass(online, status) ?? (always ? 'bg-gray-300' : null)
  if (!color) return null
  return <span aria-hidden data-presence-dot={online ? status || 'AVAILABLE' : 'OFFLINE'} className={cn('absolute rounded-full border-2 border-white', color, className)} />
}
